import DOMPurify from 'dompurify';
import { useState, useEffect, useRef, useMemo } from 'react';

/**
 * `dompurify` only ships an instance when `window` exists; on Node (during
 * RR v7 prerender) the default export is a factory and `sanitize` is undefined.
 * Returning the raw HTML during prerender lets the page render — the hydrated
 * client immediately replaces the markup with a sanitized version. Product
 * descriptions come from the admin/backoffice content pipeline, not direct
 * user input, so the build-time HTML can't carry attacker-controlled markup.
 */
function safeSanitize(html: string): string {
    if (typeof window === 'undefined') return html;
    const sanitize = (DOMPurify as unknown as { sanitize?: (input: string) => string }).sanitize;
    return typeof sanitize === 'function' ? sanitize(html) : html;
}
import { useParams } from 'react-router';
import { useProductByIdOrSlug, useAvailabilityQuery, useAvailableLocations } from '@/hooks/useCatalog';
import { useLocations } from '@/hooks/useLocations';
import type { AvailableLocationInfo } from '@/api/catalog';
import { useEquipmentTypeReviews, useEquipmentTypeReviewSummary } from '@/hooks/useReviews';
import { ProductGallery } from '../components/ProductGallery';
import { AccessoriesSection } from '../components/AccessoriesSection';
import { RelatedProductsSection } from '../components/RelatedProductsSection';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Button } from '@/components/ui/button';
import { useAddToCart } from '@/hooks/useCart';
import { useLocationStore } from '@/stores/useLocationStore';
import { useToastStore } from '@/stores/useToastStore';
import { cn, getRentalDayCount } from '@/lib/utils';
import { Loader2, Check, ShoppingCart, Minus, Plus, MapPin, AlertCircle, Info, Star } from 'lucide-react';
import { Tooltip } from '@/components/ui/Tooltip';
import type { CustomerLocationDto } from '@/types';
import { RentalDurationSelector, MonthlyRentalSelector, hasMonthlyRentalPricing, getLowestMonthlyRentalRate } from '@/components/pricing';
import type { SelectedMonthlyRentalTier } from '@/components/pricing';
import { hasTieredPricing, getLowestTierPrice } from '@/lib/pricing';
import { differenceInCalendarDays, addDays } from 'date-fns';
import { trackProductEvent } from '@/lib/telemetry/productEvents';
import { LocationRequiredModal } from '../components/LocationRequiredModal';
import { StickyBuyBar } from '../components/StickyBuyBar';
import { PricingOptionType } from '@/types/cart';
import { getRentalTierFromDays } from '@/utils/pricing';
import { useFormatPrice } from '@/hooks/useFormatPrice';

export const ProductDetail = () => {
    const { id } = useParams<{ id: string }>();
    const [quantity, setQuantity] = useState(1);
    const [selectedMonthlyRental, setSelectedMonthlyRental] = useState<SelectedMonthlyRentalTier | null>(null);
    const [monthlyRentalStartDate, setMonthlyRentalStartDate] = useState<Date | null>(null);
    const [rentalMode, setRentalMode] = useState<'short-term' | 'monthly-rental'>('short-term');
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [locationModalSource, setLocationModalSource] = useState<'pricing' | 'cart'>('cart');
    const [isNewItem, setIsNewItem] = useState(false);
    // Tracks whether the user explicitly chose a return date on this page (via the
    // disclosure inside RentalDurationSelector). When true, the date persists to
    // the cart with endDateIsExact: true so checkout shows the user's choice.
    const [returnDateExplicit, setReturnDateExplicit] = useState(false);
    const [reviewSortBy, setReviewSortBy] = useState<'newest' | 'highest'>('newest');
    const formatPrice = useFormatPrice();
    // Use the API-backed mutation hook (backend is source of truth for both rentals & subscriptions)
    const addToCartMutation = useAddToCart();
    const { success, warning, error: toastError, info } = useToastStore();
    const {
        dateRange,
        location,
        selectedTier,
        pickupDate,
        setLocation,
        setSelectedTier,
        setPickupDate,
        setDateRange
    } = useLocationStore();

    // Ref to track previous quantity for change events
    const prevQuantity = useRef(1);
    // Ref to track previous rental mode for mode change events
    const prevRentalMode = useRef<'short-term' | 'monthly-rental'>('short-term');
    // Sticky buy bar: watch when the add-to-cart actions div leaves viewport
    const buyBoxActionsRef = useRef<HTMLDivElement>(null);
    const [isBuyBoxActionsVisible, setIsBuyBoxActionsVisible] = useState(true);

    // Use smart hook that handles both GUID and slug identifiers
    const { data: product, isLoading, error } = useProductByIdOrSlug(id);
    const {
        data: reviewsData,
        isLoading: isReviewsLoading,
        hasNextPage: hasMoreReviews,
        isFetchingNextPage: isFetchingMoreReviews,
        fetchNextPage: fetchMoreReviews,
    } = useEquipmentTypeReviews(product?.id, reviewSortBy === 'highest' ? 'highest-rated' : 'newest');
    const reviews = reviewsData?.pages?.flat() ?? [];
    const { data: reviewSummary, isLoading: isReviewSummaryLoading } = useEquipmentTypeReviewSummary(product?.id);

    const clampReturnDateToTierWindow = (
        pickup: Date,
        currentReturn: Date | null | undefined,
        tier: { minDays: number; maxDays: number }
    ): Date => {
        if (!currentReturn) {
            return addDays(pickup, tier.minDays);
        }

        const currentOffsetDays = getRentalDayCount(pickup, currentReturn);
        if (currentOffsetDays < tier.minDays) {
            return addDays(pickup, tier.minDays);
        }
        if (currentOffsetDays > tier.maxDays) {
            return addDays(pickup, tier.maxDays);
        }
        return currentReturn;
    };

    // Track product view on mount (once per product)
    useEffect(() => {
        if (product) {
            trackProductEvent.viewed(
                product.id,
                product.name,
                product.categories?.[0]
            );
        }
    }, [product]);

    // Sticky buy bar: show when the add-to-cart actions div is out of view on mobile
    useEffect(() => {
        const el = buyBoxActionsRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => setIsBuyBoxActionsVisible(entry.isIntersecting),
            { threshold: 0 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    // product in deps ensures the observer re-attaches after the loading spinner unmounts
    }, [product]);

    // Determine if user has fixed dates or is using tier+pickup mode
    const hasFixedDates = dateRange.from && dateRange.to;
    const hasTierPickup = selectedTier && pickupDate;
    // Valid rental selection = either fixed dates or tier+pickup
    const hasValidRentalSelection = hasFixedDates || hasTierPickup;

    // Calculate rental days based on what's available
    const rentalDays = hasFixedDates
        ? getRentalDayCount(dateRange.from!, dateRange.to!)
        : selectedTier?.maxDays || null;

    // Derive effective dates: prefer fixed dates, fall back to tier+pickup
    const effectiveStartDate = hasFixedDates ? dateRange.from : (pickupDate ?? undefined);
    const effectiveEndDate = hasFixedDates
        ? dateRange.to
        : (pickupDate && selectedTier ? addDays(pickupDate, selectedTier.maxDays) : undefined);

    // Use the resolved equipment-type GUID (product.id), not the route param,
    // which may be a slug — the check-availability endpoint is keyed on the GUID
    // and 401s on a slug, which silently disabled the per-date stock guard below.
    const { data: availability, isLoading: isCheckingAvailability } = useAvailabilityQuery(
        product?.id,
        effectiveStartDate,
        effectiveEndDate,
        quantity
    );

    // Per-location stock check. The API returns `availableQuantity` scoped to
    // the selected location when locationId is sent — when it's zero but the
    // catalog entry itself is active, the product simply isn't carried at the
    // user's pickup location. Previously the page ignored this and rendered
    // pricing + add-to-cart as if it were available, leading customers to
    // "rent" items the location couldn't actually fulfil. See bug report
    // 2026-05-23: related-product nav from a Dublin-stocked item to a
    // non-Dublin item left the buy box looking normal.
    const notAvailableAtLocation = !!(
        location
        && product
        && product.isAvailableForRental
        && product.availableQuantity === 0
    );

    // Only fetch the alternative-location list when we know we'll need it.
    // Passing `undefined` for the equipmentTypeId disables the query (see
    // useAvailableLocations: enabled = !!equipmentTypeId).
    const { data: alternativeLocationsData, isLoading: isLoadingAlternatives } = useAvailableLocations(
        notAvailableAtLocation ? product?.id : undefined,
        effectiveStartDate?.toISOString(),
        effectiveEndDate?.toISOString(),
        quantity,
    );
    // Use the full-location list to resolve a complete CustomerLocationDto for
    // setLocation — matches the pattern in LocationRequiredModal.
    const { data: allLocationsForSwitch } = useLocations();
    const alternativeLocations = useMemo<AvailableLocationInfo[]>(
        () => (alternativeLocationsData?.locations ?? []).filter(
            (loc) => loc.isAvailable && loc.availableQuantity > 0 && loc.locationId !== location?.id,
        ),
        [alternativeLocationsData, location?.id],
    );

    const handleSwitchToAlternativeLocation = (loc: AvailableLocationInfo) => {
        const full = allLocationsForSwitch?.find((candidate) => candidate.id === loc.locationId);
        const resolved: CustomerLocationDto = full ?? ({
            id: loc.locationId,
            name: loc.locationName,
            city: loc.city,
            state: loc.state,
            country: loc.country,
        } as CustomerLocationDto);
        setLocation(resolved);
        // The product query is keyed by location.id — switching triggers a
        // refetch with the new locationId, so the buy box will re-render with
        // accurate pricing and availability automatically.
    };

    // Calculate effective deposit based on selected pricing option
    const effectiveDeposit = useMemo(() => {
        // If product has a fixed deposit amount, use it
        if (product?.depositAmount != null && product.depositAmount > 0) {
            return product.depositAmount;
        }

        // If product has a percentage deposit, calculate based on selected pricing
        if (product?.depositPercent != null && product.depositPercent > 0) {
            let rentalPrice: number | null = null;

            if (rentalMode === 'monthly-rental' && selectedMonthlyRental) {
                // For monthly rentals, use the monthly rate
                rentalPrice = selectedMonthlyRental.monthlyRate;
            } else if (selectedTier) {
                // For short-term rentals, use the selected tier price
                rentalPrice = selectedTier.price;
            }

            // If we have a rental price, calculate percentage-based deposit
            if (rentalPrice != null && rentalPrice > 0) {
                return (rentalPrice * product.depositPercent) / 100;
            }
        }

        // No deposit configured or no pricing selected
        return null;
    }, [product, rentalMode, selectedMonthlyRental, selectedTier]);

    if (isLoading) {
        return (
            <div className="flex h-[50vh] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (error || !product) {
        return (
            <div className="container mx-auto px-4 py-16 text-center">
                <h1 className="text-2xl font-bold text-foreground">Product not found</h1>
                <p className="mt-2 text-muted-foreground">The product you are looking for does not exist or has been removed.</p>
            </div>
        );
    }

    const handleTierSelect = (tier: { tierName: string; minDays: number; maxDays: number; price: number }) => {
        if (product) {
            trackProductEvent.tierSelected(
                product.id,
                tier.tierName,
                tier.minDays,
                tier.maxDays,
                tier.price
            );
        }
        setSelectedTier(tier);
        // A tier change invalidates any prior explicit return-date pick — the new tier
        // may not contain the previous date, so revert to the tier-default window.
        setReturnDateExplicit(false);
        // Keep pickup date stable and clamp the return date to the selected tier window.
        const effectivePickupDate = dateRange.from ?? pickupDate;
        if (effectivePickupDate) {
            const adjustedReturn = clampReturnDateToTierWindow(effectivePickupDate, dateRange.to, tier);
            setDateRange({ from: effectivePickupDate, to: adjustedReturn });
        }
    };

    const handlePickupDateChange = (date: Date | null) => {
        if (product && date) {
            trackProductEvent.pickupDateSelected(product.id, date.toISOString());
        }
        setPickupDate(date);
        // Keep catalog date filter in sync: when a pickup date is chosen and we already have a
        // selected tier, update the shared dateRange so the header picker stays consistent.
        if (date && selectedTier) {
            const adjustedReturn = clampReturnDateToTierWindow(date, dateRange.to, selectedTier);
            setDateRange({ from: date, to: adjustedReturn });
        }
    };

    const handleReturnDateChange = (date: Date | null) => {
        const effectivePickupDate = dateRange.from ?? pickupDate;
        if (!effectivePickupDate || !selectedTier) {
            return;
        }
        if (date === null) {
            // Reset to tier default (pickup + tier.minDays).
            setReturnDateExplicit(false);
            setDateRange({ from: effectivePickupDate, to: addDays(effectivePickupDate, selectedTier.minDays) });
            return;
        }
        // Clamp into the tier window defensively even though the input enforces min/max.
        const clamped = clampReturnDateToTierWindow(effectivePickupDate, date, selectedTier);
        setReturnDateExplicit(true);
        setDateRange({ from: effectivePickupDate, to: clamped });
    };

    const handleQuantityChange = (newQty: number) => {
        if (product && newQty !== prevQuantity.current) {
            trackProductEvent.quantityChanged(product.id, prevQuantity.current, newQty);
            prevQuantity.current = newQty;
        }
        setQuantity(newQty);
    };

    const handleMonthlyRentalSelect = (tier: SelectedMonthlyRentalTier | null) => {
        if (product) {
            const newMode = tier ? 'monthly-rental' : 'short-term';
            if (newMode !== prevRentalMode.current) {
                trackProductEvent.modeChanged(product.id, prevRentalMode.current, newMode);
                prevRentalMode.current = newMode;
            }
            if (tier) {
                trackProductEvent.subscriptionSelected(product.id, tier.months, tier.monthlyRate);
            }
        }
        setSelectedMonthlyRental(tier);
        if (tier) {
            setRentalMode('monthly-rental');
            setSelectedTier(null);
        } else {
            setRentalMode('short-term');
            setMonthlyRentalStartDate(null);
        }
    };

    const handleAddToCart = (overrideLocation?: CustomerLocationDto) => {
        const effectiveLocation = overrideLocation || location;

        // Enforce location selection FIRST (before all other checks)
        if (!effectiveLocation) {
            trackProductEvent.addToCartClicked(product.id, false, 'no_location_selected');
            setLocationModalSource('cart');
            setShowLocationModal(true);
            return;
        }

        // Check if product is available for rental
        if (!product.isAvailableForRental) {
            warning("This item is currently unavailable for rental.");
            return;
        }

        // Per-location stock check (see notAvailableAtLocation comment above).
        // The button is also disabled visually, but the keyboard / sticky-bar
        // path can still reach this handler, so block here too.
        if (effectiveLocation && product.availableQuantity === 0) {
            warning(`This item isn't available at ${effectiveLocation.name}. Pick a different pickup location.`);
            return;
        }

        // Handle monthly rental mode
        if (rentalMode === 'monthly-rental') {
            if (!selectedMonthlyRental) {
                trackProductEvent.addToCartClicked(product.id, false, 'no_monthly_rental_selected');
                warning("Please choose a monthly rental option.");
                return;
            }

            if (!monthlyRentalStartDate) {
                trackProductEvent.addToCartClicked(product.id, false, 'no_pickup_date');
                warning("Please choose a start date for your rental.");
                return;
            }

            // Use API-backed mutation for monthly rentals (backend is source of truth)
            addToCartMutation.mutate({
                equipmentId: product.id,
                quantity: quantity,
                startDate: monthlyRentalStartDate.toISOString().split('T')[0],
                locationId: effectiveLocation.id,
                pricingOptionType: PricingOptionType.MonthlySubscription,
                subscriptionMonths: selectedMonthlyRental.months,
                isNewItem: isNewItem,
            }, {
                onSuccess: () => {
                    trackProductEvent.addToCartClicked(product.id, true);
                    success(`${product.name} monthly rental added to cart!`);
                },
                onError: () => {
                    trackProductEvent.addToCartClicked(product.id, false, 'api_error');
                }
            });
            return;
        }

        // Validate: user needs either fixed dates OR tier+pickup date
        if (hasFixedDates) {
            // Fixed dates already validated by truthiness check above
        } else if (hasTierPickup) {
            // Tier + pickup date is sufficient — return date inferred from tier maxDays
        } else if (selectedTier && !pickupDate) {
            trackProductEvent.addToCartClicked(product.id, false, 'no_pickup_date');
            warning("Please select a pickup date.");
            return;
        } else if (!selectedTier && pickupDate) {
            trackProductEvent.addToCartClicked(product.id, false, 'no_tier_selected');
            warning("Please select a rental duration.");
            return;
        } else {
            trackProductEvent.addToCartClicked(product.id, false, 'no_dates_selected');
            warning("Please select a rental duration and pickup date, or set specific dates.");
            return;
        }

        if (availability && !availability.isAvailable) {
            trackProductEvent.addToCartClicked(product.id, false, 'not_available');
            toastError("This item is not available for the selected dates.");
            return;
        }

        const startDate = effectiveStartDate!;
        const endDate = effectiveEndDate!;

        // Use the user's selected tier if explicitly chosen, otherwise calculate from days
        const days = Math.max(1, differenceInCalendarDays(endDate, startDate));
        const rentalTierEnum = selectedTier
            ? getRentalTierFromDays(selectedTier.minDays)
            : getRentalTierFromDays(days);

        // Use API-backed mutation hook for regular rentals (backend as source of truth)
        addToCartMutation.mutate({
            equipmentId: product.id,
            quantity: quantity,
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            // Mark the end date as exact when the customer either entered fixed dates
            // upfront or explicitly chose a return date via the on-page disclosure.
            endDateIsExact: !!hasFixedDates || returnDateExplicit,
            locationId: effectiveLocation.id,
            // Pricing option fields
            pricingOptionType: PricingOptionType.Rental,
            selectedRentalTier: rentalTierEnum,
            // New item pricing
            isNewItem: isNewItem,
            // Deprecated but kept for backwards compatibility
            unitPrice: product.price1Day || 0,
        }, {
            onSuccess: () => {
                trackProductEvent.addToCartClicked(product.id, true);
                success(`${product.name} added to cart!`);
            },
            onError: () => {
                trackProductEvent.addToCartClicked(product.id, false, 'api_error');
            }
        });
    };

    const handleLocationSelected = (selectedLocation: CustomerLocationDto) => {
        setLocation(selectedLocation);
        // Only auto-add to cart if the modal was triggered from the add-to-cart button
        if (locationModalSource === 'cart') {
            // Pass location directly to avoid stale closure from setTimeout
            handleAddToCart(selectedLocation);
        }
        // If opened from pricing prompt, just setting the location is enough.
        // The query key includes locationId, so prices will auto-refetch.
    };

    const handleUpdateDatesClick = () => {
        if (!selectedTier) {
            info("Select a rental duration first.");
            return;
        }

        const effectivePickupDate = dateRange.from ?? pickupDate;
        if (!effectivePickupDate) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            info("Use the date selector at the top to pick your pickup date.");
            return;
        }

        const adjustedReturn = clampReturnDateToTierWindow(effectivePickupDate, dateRange.to, selectedTier);
        setDateRange({ from: effectivePickupDate, to: adjustedReturn });
        info("Return date updated to match your selected rental duration.");
    };

    const handleLocationRequired = () => {
        setLocationModalSource('pricing');
        setShowLocationModal(true);
    };

    const images = product.imageUrls && product.imageUrls.length > 0
        ? product.imageUrls
        : (product.primaryImageUrl ? [product.primaryImageUrl] : []);

    // Determine if add to cart should be disabled
    const isProductUnavailable = !product.isAvailableForRental || (!hasTieredPricing(product) && !hasMonthlyRentalPricing(product));

    const isAddToCartDisabled = isProductUnavailable || notAvailableAtLocation || (rentalMode === 'monthly-rental'
        ? !selectedMonthlyRental || !monthlyRentalStartDate
        : !hasValidRentalSelection || isCheckingAvailability || (availability && !availability.isAvailable));

    const renderDescriptionSection = () => {
        const sanitizedDescription = safeSanitize(
            (product.productPageDescription || product.detailedDescription || product.shortDescription || '')
                .replace(/&nbsp;/g, ' ')
                .replace(/\u00A0/g, ' ')
        );

        if (!sanitizedDescription) {
            return null;
        }

        return (
            <details open className="mt-6 border border-border rounded-lg bg-white">
                <summary className="cursor-pointer select-none px-4 py-3 text-xl font-semibold text-foreground">
                    Description
                </summary>
                <div
                    className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_p]:mb-3 [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-medium [&_h3]:mb-2 [&_strong]:font-semibold [&_a]:text-primary [&_a]:underline"
                    dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
                />
            </details>
        );
    };

    const renderAccessoriesSection = () => (
        <AccessoriesSection
            equipmentTypeId={product.id}
            title="Complete Your Rental"
            maxItems={6}
            variant="panel"
            panelTitle="Accessories"
        />
    );

    const renderSpecificationsSection = () => {
        const hasSpecs = Boolean(product.ageRange || product.weightRange || product.heightRange);

        if (!hasSpecs) {
            return null;
        }

        return (
            <details open className="mt-4 border border-border rounded-lg bg-white">
                <summary className="cursor-pointer select-none px-4 py-3 text-xl font-semibold text-foreground">
                    Specifications
                </summary>
                <div className="px-4 pb-4">
                    {(product.ageRange || product.weightRange) && (
                        <p className="mb-4 rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
                            Age and weight ranges are approximate recommendations. Children can vary in size for their age,
                            so always confirm fit and manufacturer limits before use.
                        </p>
                    )}
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                        {product.ageRange && (
                            <div className="border-t border-border pt-4">
                                <dt className="font-medium text-foreground">Age Range</dt>
                                <dd className="mt-2 text-sm text-muted-foreground">{product.ageRange}</dd>
                            </div>
                        )}
                        {product.weightRange && (
                            <div className="border-t border-border pt-4">
                                <dt className="font-medium text-foreground">Weight Range</dt>
                                <dd className="mt-2 text-sm text-muted-foreground">{product.weightRange}</dd>
                            </div>
                        )}
                        {product.heightRange && (
                            <div className="border-t border-border pt-4">
                                <dt className="font-medium text-foreground">Height Range</dt>
                                <dd className="mt-2 text-sm text-muted-foreground">{product.heightRange}</dd>
                            </div>
                        )}
                    </dl>
                </div>
            </details>
        );
    };

    const renderDetails = () => (
        <>
            {/* Features */}
            {product.features && product.features.length > 0 && (
                <div className="mt-10 border-t border-border pt-10">
                    <h3 className="text-lg font-medium text-foreground">Features</h3>
                    <ul className="mt-4 list-disc pl-4 space-y-2">
                        {product.features.map((feature, index) => (
                            <li key={index} className="text-sm text-muted-foreground">{feature}</li>
                        ))}
                    </ul>
                </div>
            )}
        </>
    );

    const renderReviewsSection = () => {
        const averageRating = reviewSummary?.averageRating ?? 0;
        const reviewCount = reviewSummary?.approvedReviewCount ?? 0;

        return (
            <section className="mt-10 border-t border-border pt-10" aria-label="Product reviews">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-5">
                    <div>
                        <h3 className="text-lg font-medium text-foreground">Reviews</h3>
                        <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                            <span className="font-semibold text-foreground">{averageRating.toFixed(1)}</span>
                            <div className="flex items-center gap-0.5" aria-hidden="true">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <Star
                                        key={star}
                                        className={cn(
                                            'h-4 w-4',
                                            averageRating >= star ? 'fill-warning text-warning' : 'text-muted-foreground/50'
                                        )}
                                    />
                                ))}
                            </div>
                            <span>
                                ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
                            </span>
                        </div>
                    </div>
                    <label className="text-sm text-muted-foreground inline-flex items-center gap-2">
                        Sort
                        <select
                            value={reviewSortBy}
                            onChange={(event) => setReviewSortBy(event.target.value as 'newest' | 'highest')}
                            className="rounded-md border border-border bg-white px-2 py-1 text-sm"
                        >
                            <option value="newest">Newest</option>
                            <option value="highest">Highest Rated</option>
                        </select>
                    </label>
                </div>

                {(isReviewsLoading || isReviewSummaryLoading) && (
                    <p className="text-sm text-muted-foreground">Loading reviews...</p>
                )}

                {!isReviewsLoading && reviewCount === 0 && (
                    <p className="text-sm text-muted-foreground">
                        No reviews yet — be the first to share your experience after your rental.
                    </p>
                )}

                {reviews.length > 0 && (
                    <div className="space-y-3">
                        {reviews.map((review) => (
                            <article key={review.reviewId} className="rounded-lg border border-border bg-white p-4">
                                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                    <p className="font-medium text-foreground">{review.reviewerDisplayName || 'Anonymous'}</p>
                                    <div className="flex items-center gap-0.5">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <Star
                                                key={`${review.reviewId}-${star}`}
                                                className={cn(
                                                    'h-4 w-4',
                                                    (review.rating ?? 0) >= star ? 'fill-warning text-warning' : 'text-muted-foreground/50'
                                                )}
                                            />
                                        ))}
                                    </div>
                                </div>
                                {review.reviewText && (
                                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{review.reviewText}</p>
                                )}
                                {review.updatedAtUtc && (
                                    <p className="text-xs text-muted-foreground/70 mt-3">
                                        {new Date(review.updatedAtUtc).toLocaleDateString()}
                                    </p>
                                )}
                            </article>
                        ))}
                    </div>
                )}

                {hasMoreReviews && (
                    <Button
                        variant="outline"
                        className="mt-4"
                        disabled={isFetchingMoreReviews}
                        onClick={() => fetchMoreReviews()}
                    >
                        {isFetchingMoreReviews ? 'Loading…' : 'Show More Reviews'}
                    </Button>
                )}
            </section>
        );
    };

    const renderRelatedProductsSection = () => (
        <RelatedProductsSection
            equipmentTypeId={product.id}
            title="You Might Also Like"
            maxItems={4}
        />
    );

    const renderBuyBox = () => {
        const showTieredPricing = hasTieredPricing(product);
        const showMonthlyRentalPricing = hasMonthlyRentalPricing(product);
        const lowestPrice = getLowestTierPrice(product);

        return (
        <div className="mt-8 lg:mt-0 lg:sticky lg:top-24">
            {product.brand && (
                <p className="text-sm font-medium text-primary mb-1">{product.brand}</p>
            )}
            <h1 className="text-3xl font-bold tracking-tight text-foreground">{product.name}</h1>

            {/* Unavailable Banner */}
            {!product.isAvailableForRental && (
                <div className="mt-4 p-4 bg-warning-subtle border border-warning-border rounded-lg">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium text-warning-foreground">Currently Unavailable</p>
                            <p className="text-sm text-warning-foreground/80 mt-1">
                                This item is not available for rental at this time. Please check back later or browse our other products.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Per-location unavailability banner — shows when the catalog
                entry IS active but the user's selected location has 0 stock.
                Lists alternative pickup locations so the user can switch in
                one click instead of navigating away or getting stuck. */}
            {notAvailableAtLocation && location && (
                <div className="mt-4 p-4 bg-warning-subtle border border-warning-border rounded-lg">
                    <div className="flex items-start gap-3">
                        <MapPin className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-warning-foreground">
                                Not available at {location.name}
                            </p>
                            <p className="text-sm text-warning-foreground/80 mt-1">
                                {effectiveStartDate && effectiveEndDate
                                    ? `Sorry — this item is out of stock at ${location.name} for the dates you selected.`
                                    : `Sorry — this item isn't currently stocked at ${location.name}.`}
                            </p>
                            {isLoadingAlternatives ? (
                                <p className="text-sm text-warning-foreground/80 mt-3">Checking other pickup locations…</p>
                            ) : alternativeLocations.length > 0 ? (
                                <div className="mt-3">
                                    <p className="text-sm font-medium text-foreground mb-2">
                                        Try another pickup location:
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {alternativeLocations.map((alt) => (
                                            <button
                                                key={alt.locationId}
                                                type="button"
                                                onClick={() => handleSwitchToAlternativeLocation(alt)}
                                                className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-white px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
                                            >
                                                <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                                                {alt.locationName}
                                                <span className="text-xs text-muted-foreground">
                                                    ({alt.availableQuantity} available)
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-warning-foreground/80 mt-3">
                                    This item isn&apos;t available at any of our pickup locations right now. Please check back later or browse our other products.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Price Header — hidden when not stocked at the selected location;
                the alternative-location picker above gives the next action. */}
            {!notAvailableAtLocation && (
            <div className="mt-3">
                <h2 className="sr-only">Product information</h2>
                {product.isRefurbished ? (
                        <p className="text-3xl tracking-tight text-foreground">
                        {formatPrice(product.price1Day || 0)}
                        <span className="text-sm font-normal text-muted-foreground ml-2">sale price</span>
                    </p>
                ) : !location ? (
                    <p className="text-xl tracking-tight text-muted-foreground">
                        Select a location to see pricing
                    </p>
                ) : showTieredPricing && lowestPrice ? (
                    <div className="space-y-1">
                        <p className="text-3xl tracking-tight text-foreground">
                            From {formatPrice(lowestPrice)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                            Select rental duration below
                        </p>
                    </div>
                ) : (
                    <p className="text-3xl tracking-tight text-foreground">
                        Contact for Price
                    </p>
                )}
            </div>
            )}

            {location && product.isAvailableForRental && !notAvailableAtLocation && !product.isRefurbished && (
                <div className="mt-4 flex items-start gap-3 rounded-[1.25rem] border border-border/80 bg-secondary/70 px-4 py-3 shadow-[0_14px_34px_rgba(54,49,43,0.08)]">
                    <span className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white/85 text-primary shadow-sm">
                        <MapPin className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                        <p className="text-[0.625rem] font-bold uppercase tracking-[0.18em] text-primary/80">Pickup in</p>
                        <p className="truncate font-display text-xl leading-tight text-foreground">{location.name}</p>
                        <p className="mt-1 text-sm text-muted-foreground">You&apos;ll collect your items here.</p>
                    </div>
                </div>
            )}

            {/* Location Selection Prompt - shown when no location is selected */}
            {!location && product.isAvailableForRental && !product.isRefurbished && (
                <div className="mt-6 p-4 bg-muted rounded-lg border border-border">
                    <p className="font-medium text-foreground">Where would you like to pick up?</p>
                    <p className="text-sm text-muted-foreground mt-1">
                        Select a location to see prices and availability.
                    </p>
                    <Button
                        className="mt-3 gap-2"
                        onClick={handleLocationRequired}
                    >
                        <MapPin className="h-4 w-4" />
                        Select Location
                    </Button>
                </div>
            )}

            {/* New/Pre-owned Toggle - Only shown when location selected and new item rental is available.
                `product.availableQuantity > 0` already excludes the per-location-out-of-stock case. */}
            {location && product.allowNewItemRental && product.availableQuantity > 0 && !product.isRefurbished && (
                <div className="mt-6 p-4 bg-muted rounded-lg border border-border">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-foreground">Item Condition</span>
                    </div>
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={() => setIsNewItem(false)}
                            className={cn(
                                "flex-1 px-4 py-3 rounded-lg border-2 font-medium text-sm transition-all",
                                !isNewItem
                                    ? "border-primary bg-primary/5 text-primary"
                                    : "border-border bg-white text-muted-foreground hover:border-border"
                            )}
                        >
                            <div className="flex items-center justify-center gap-2">
                                {!isNewItem && <Check className="h-4 w-4" />}
                                <span>Pre-owned</span>
                            </div>
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsNewItem(true)}
                            className={cn(
                                "flex-1 px-4 py-3 rounded-lg border-2 font-medium text-sm transition-all",
                                isNewItem
                                    ? "border-primary bg-primary/5 text-primary"
                                    : "border-border bg-white text-muted-foreground hover:border-border"
                            )}
                        >
                            <div className="flex items-center justify-center gap-2">
                                {isNewItem && <Check className="h-4 w-4" />}
                                <span>Brand New</span>
                            </div>
                        </button>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground text-center">
                        {isNewItem ? "Never rented - premium pricing applies" : "Gently used - standard pricing"}
                    </p>
                </div>
            )}

            {/* Quantity & Add to Cart - shown above pricing tiers */}
            {location && !notAvailableAtLocation && (
            <>
            <div ref={buyBoxActionsRef} className="mt-6 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-4">
                <div className="flex w-full items-center justify-center border border-border rounded-md sm:w-auto sm:justify-start">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 rounded-none"
                        onClick={() => handleQuantityChange(Math.max(1, quantity - 1))}
                        disabled={quantity <= 1}
                        aria-label="Decrease quantity"
                    >
                        <Minus className="h-4 w-4" />
                    </Button>
                    <div className="w-12 text-center font-medium">{quantity}</div>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 rounded-none"
                        onClick={() => handleQuantityChange(quantity + 1)}
                        disabled={availability ? quantity >= availability.availableQuantity : false}
                        aria-label="Increase quantity"
                    >
                        <Plus className="h-4 w-4" />
                    </Button>
                </div>

                <Button
                    size="lg"
                    className="w-full gap-2 min-h-[44px] sm:flex-1"
                    onClick={() => handleAddToCart()}
                    disabled={isAddToCartDisabled}
                >
                    <ShoppingCart className="h-5 w-5" />
                    {isProductUnavailable ? 'Currently Unavailable' : 'Add to Cart'}
                </Button>
            </div>

            </>
            )}

            {/* Rental Duration Selector - Main pricing UI (requires location, and meaningful only when stocked there) */}
            {location && !notAvailableAtLocation && showTieredPricing && !product.isRefurbished && (
                <div className="mt-6">
                    <RentalDurationSelector
                        product={product}
                        preSelectedDays={hasFixedDates ? rentalDays : null}
                        fixedDatesMode={!!hasFixedDates}
                        pickupDate={hasFixedDates ? dateRange.from : pickupDate}
                        returnDate={hasFixedDates ? dateRange.to : undefined}
                        initialTier={!hasFixedDates ? selectedTier : null}
                        isSelected={rentalMode === 'short-term'}
                        onUpdateDatesClick={handleUpdateDatesClick}
                        requiresLocation={!location}
                        onLocationRequired={handleLocationRequired}
                        isNewItem={isNewItem}
                        onTierSelect={(tier) => {
                            handleTierSelect(tier);
                            // Track mode change if switching from monthly rental
                            if (prevRentalMode.current === 'monthly-rental') {
                                trackProductEvent.modeChanged(product.id, 'monthly-rental', 'short-term');
                            }
                            prevRentalMode.current = 'short-term';
                            setRentalMode('short-term');
                            setSelectedMonthlyRental(null);
                        }}
                        onPickupDateChange={handlePickupDateChange}
                        onReturnDateChange={handleReturnDateChange}
                    />
                </div>
            )}

            {/* Monthly Rental Pricing Section (requires location, and meaningful only when stocked there) */}
            {location && !notAvailableAtLocation && showMonthlyRentalPricing && !product.isRefurbished && (
                <div className="mt-5 rounded-lg border border-border bg-white px-4 py-3">
                    <p className="text-sm font-medium text-foreground">
                        Monthly rental — from {formatPrice(getLowestMonthlyRentalRate(product) ?? 0)}/month
                    </p>
                    <div className="pt-3">
                        <MonthlyRentalSelector
                            product={product}
                            selectedTier={selectedMonthlyRental}
                            onTierSelect={handleMonthlyRentalSelect}
                            startDate={monthlyRentalStartDate}
                            onStartDateChange={setMonthlyRentalStartDate}
                            requiresLocation={!location}
                            onLocationRequired={handleLocationRequired}
                            isNewItem={isNewItem}
                        />
                    </div>
                </div>
            )}

            {/* Deposit Information - Show when location selected and deposit is required */}
            {location && !notAvailableAtLocation && effectiveDeposit != null && effectiveDeposit > 0 && (
                <div className="mt-4 p-4 bg-primary/5 rounded-lg border border-primary/30">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium text-primary">
                                Refundable deposit
                            </span>
                            <Tooltip
                                content="This refundable security deposit protects the equipment during your rental. It's charged when you place your order and fully refunded after the item is returned and inspected (usually within a few business days)."
                                position="top"
                                maxWidth="420px"
                            >
                                <button
                                    type="button"
                                    className="text-primary hover:text-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 rounded-full"
                                    aria-label="Learn more about the refundable deposit"
                                >
                                    <Info className="h-4 w-4" />
                                </button>
                            </Tooltip>
                        </div>
                        <span className="text-sm font-semibold text-primary">
                            {formatPrice(effectiveDeposit)}
                            {product.depositPercent != null && product.depositPercent > 0 && (
                                <span className="text-xs font-normal text-primary ml-1">({product.depositPercent}%)</span>
                            )}
                        </span>
                    </div>
                    <p className="text-xs text-primary mt-1">
                        Charged when you place your order, refunded after return inspection
                    </p>
                </div>
            )}
        </div>
    );
    };

    return (
        <>
            {/*
              * SEO + JSON-LD for product detail lives in the route shell's
              * `meta` export (`app/routes/$locale.product.$id.tsx`). Dynamic
              * extras like aggregateRating / category breadcrumbs that depend
              * on runtime data are intentionally omitted there — they can be
              * added back via the route loader in a follow-up.
              */}

            <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
                {/* Breadcrumb Navigation */}
                <Breadcrumb
                    items={[
                        { label: 'Catalogue', href: '/catalogue' },
                        ...(product.categories && product.categories.length > 0
                            ? [{ label: product.categories[0], href: `/catalogue?category=${encodeURIComponent(product.categories[0])}` }]
                            : []),
                        { label: product.name }
                    ]}
                />

                {/* Desktop: 2x2 Grid Layout */}
                <div className="hidden lg:grid lg:grid-cols-2 lg:gap-x-8 lg:gap-y-4">
                    {/* Row 1, Col 1: Product Gallery */}
                    <div className="min-w-0 overflow-hidden">
                        <ProductGallery images={images} title={product.name} />
                        {renderAccessoriesSection()}
                        {renderDescriptionSection()}
                        {renderSpecificationsSection()}
                    </div>

                    {/* Row 1, Col 2: Buy Box */}
                    <div className="lg:sticky lg:top-24 lg:self-start min-w-0">
                        {renderBuyBox()}
                    </div>

                    {/* Row 2, Col 1: Details (Specs, Features) */}
                    <div className="min-w-0 overflow-hidden">
                        {renderDetails()}
                        {renderReviewsSection()}
                    </div>


                </div>

                {/* Related Products - Full width below the 2-column layout */}
                <div className="hidden lg:block">
                    {renderRelatedProductsSection()}
                </div>

                {/* Mobile: Single Column Layout.
                    Buy box sits IMMEDIATELY after the gallery so the user sees
                    the title, brand, and price right after the hero image —
                    matches Amazon / Etsy / Shopify mobile conventions. The
                    previous order buried the buy box behind Accessories +
                    Description + Specifications, which meant the user had to
                    scroll past three sections before they even knew what the
                    product was called or what it cost. */}
                <div className="lg:hidden">
                    <ProductGallery images={images} title={product.name} />
                    {renderBuyBox()}
                    {renderDescriptionSection()}
                    {renderSpecificationsSection()}
                    {renderAccessoriesSection()}
                    {renderDetails()}
                    {renderReviewsSection()}
                    {renderRelatedProductsSection()}
                </div>

                {/* Location Required Modal */}
                <LocationRequiredModal
                    isOpen={showLocationModal}
                    onClose={() => setShowLocationModal(false)}
                    onLocationSelected={handleLocationSelected}
                    productName={product.name}
                    equipmentTypeId={product.id}
                    startDate={dateRange.from?.toISOString().split('T')[0]}
                    endDate={dateRange.to?.toISOString().split('T')[0]}
                    quantity={quantity}
                />
            </div>

            {/* Sticky buy bar — mobile only, appears when add-to-cart button scrolls out of view */}
            <StickyBuyBar
                isVisible={!isBuyBoxActionsVisible}
                productName={product.name}
                lowestPrice={getLowestTierPrice(product)}
                onAddToCart={() => handleAddToCart()}
                isDisabled={!!isAddToCartDisabled}
                isLoading={addToCartMutation.isPending ?? false}
            />
        </>
    );
};
